(() => {
  'use strict';
  const BRANCHES = {
    light: ['nuts', 'floral', 'ripe_fruit', 'milky'],
    rounded: ['floral', 'ripe_fruit', 'bamboo', 'fruity', 'lychee'],
    rich: ['nuts', 'floral', 'ripe_fruit', 'woody'],
    roasted_aged: ['floral', 'ripe_fruit', 'woody', 'jujube', 'herbal']
  };
  const TASTES = ['returning_sweet', 'bitter', 'sweet', 'umami'];
  const DIMENSIONS = ['texture', 'aroma', 'taste'];
  function photoGeometry(count, shape) {
    if (![3,4,5,6].includes(count)) return [];
    const fixed = value => Number(value.toFixed(4));
    return Array.from({length:count}, (_, index) => {
      let points, marker;
      if (shape === 'square' && count !== 4) {
        const start = -Math.PI / 2 + index * Math.PI * 2 / count;
        const end = start + Math.PI * 2 / count;
        const clip = (vertices, angle, sign) => {
          const dx = Math.cos(angle), dy = Math.sin(angle), result = [];
          const distance = point => sign * (dx * (point[1]-50) - dy * (point[0]-50));
          vertices.forEach((point, position) => {
            const next = vertices[(position+1)%vertices.length], a = distance(point), b = distance(next);
            if (a >= -1e-8) result.push(point);
            if ((a >= -1e-8) !== (b >= -1e-8)) {
              const t = a / (a-b);
              result.push([point[0]+t*(next[0]-point[0]),point[1]+t*(next[1]-point[1])]);
            }
          });
          return result;
        };
        points = clip(clip([[0,0],[100,0],[100,100],[0,100]],start,1),end,-1);
        let area = 0, x = 0, y = 0;
        points.forEach((point, position) => {
          const next = points[(position+1)%points.length], cross = point[0]*next[1]-next[0]*point[1];
          area += cross; x += (point[0]+next[0])*cross; y += (point[1]+next[1])*cross;
        });
        marker = {x:x/(3*area),y:y/(3*area)};
      } else if (shape === 'square') {
        const topCount = Math.ceil(count / 2), bottomCount = count - topCount;
        const top = index < topCount, columns = top ? topCount : bottomCount;
        const column = top ? index : count - index - 1;
        const x = column * 100 / columns, y = top ? 0 : topCount * 100 / count;
        const width = 100 / columns, height = (top ? topCount : bottomCount) * 100 / count;
        points = [[x,y],[x+width,y],[x+width,y+height],[x,y+height]];
        marker = {x:x+width/2,y:y+height/2};
      } else {
        const angle = Math.PI * 2 / count, start = -Math.PI + index * angle;
        points = [[50,50], ...Array.from({length:37}, (_, sample) => {
          const a = start + sample * angle / 36;
          return [50 + 50 * Math.cos(a),50 + 50 * Math.sin(a)];
        })];
        const center = start + angle / 2, radius = 200 * Math.sin(angle / 2) / (3 * angle);
        marker = {x:50+radius*Math.cos(center),y:50+radius*Math.sin(center)};
      }
      const xs = points.map(point => point[0]), ys = points.map(point => point[1]);
      const left = Math.min(...xs), top = Math.min(...ys);
      return {outline:points.map(point => point.map(fixed).join(',')).join(' '),
        clip:'polygon(' + points.map(point => point.map(value => fixed(value)+'%').join(' ')).join(',') + ')',
        bounds:{x:fixed(left),y:fixed(top),width:fixed(Math.max(...xs)-left),height:fixed(Math.max(...ys)-top)},
        marker:{x:fixed(marker.x),y:fixed(marker.y)}};
    });
  }
  const list = value => (Array.isArray(value) ? value : String(value || '').split(',')).map(item => String(item).trim()).filter(Boolean);
  function validAnswers(answers) {
    return !!BRANCHES[answers.texture] && BRANCHES[answers.texture].includes(answers.aroma) && TASTES.includes(answers.taste);
  }
  function matchingProducts(products, answers) {
    return products.filter(product => product.available && DIMENSIONS.every(key => {
      const scoped = key === 'aroma' ? list(product.aromas_by_texture?.[answers.texture]) : [];
      return !answers[key] || (scoped.length ? scoped : list(product[key + 's'])).includes(answers[key]);
    }));
  }
  function canChoose(products, answers, dimension, value) {
    const index = DIMENSIONS.indexOf(dimension);
    if (index < 0) return false;
    const next = Object.fromEntries(DIMENSIONS.slice(0,index).map(key => [key,answers[key]]));
    next[dimension] = value;
    if (!BRANCHES[next.texture]) return false;
    const aromas = BRANCHES[next.texture];
    if (index > 0 && !aromas.includes(next.aroma)) return false;
    if (index === 2) return TASTES.includes(next.taste) && matchingProducts(products,next).length > 0;
    return (index === 0 ? aromas : [next.aroma]).some(aroma => TASTES.some(taste => matchingProducts(products,{...next,aroma,taste}).length > 0));
  }
  function rankProducts(products, answers) {
    if (!validAnswers(answers)) return [];
    return matchingProducts(products,answers).map(product => ({product,matches:[...DIMENSIONS],score:100}))
      .sort((a, b) => a.product.priority - b.product.priority || a.product.index - b.product.index)
      .filter((item, index, rows) => rows.findIndex(row => row.product.id === item.product.id) === index);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {BRANCHES, TASTES, validAnswers, matchingProducts, canChoose, rankProducts, photoGeometry};
  if (typeof document === 'undefined') return;
  class YunshoTeaFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { window.addEventListener('hashchange', this.onHashChange); this.observeQuestionHeadings(); this.readRoute(false); return; }
      try { this.data = JSON.parse(this.querySelector('[data-finder-data]').textContent); } catch (_) { return; }
      this.dataset.photoLayout = new URLSearchParams(location.search || '').get('finder_layout') === 'circle' ? 'circle' : 'square';
      const decode = value => value.replace(/&(amp|quot|#39|lt|gt);/g, (_, key) => ({amp:'&',quot:'"','#39':"'",lt:'<',gt:'>'})[key]);
      Object.keys(this.data.strings).forEach(key => { this.data.strings[key] = decode(this.data.strings[key]); });
      this.answers = {texture:'', aroma:'', taste:''}; this.step = 0; this.initialized = true;
      this.addEventListener('click', event => this.onClick(event));
      this.onHashChange = () => this.readRoute(true); window.addEventListener('hashchange', this.onHashChange);
      this.classList.add('is-ready'); this.observeQuestionHeadings(); this.readRoute(false);
    }
    disconnectedCallback() { window.removeEventListener('hashchange', this.onHashChange); this.questionHeadObserver?.disconnect(); }
    observeQuestionHeadings() {
      if (typeof ResizeObserver === 'undefined') return;
      this.questionHeadObserver ||= new ResizeObserver(() => this.layoutQuestionHeading());
      this.querySelectorAll('.tea-finder__question-head,.tea-finder__intro,.tea-finder__steps').forEach(heading => this.questionHeadObserver.observe(heading));
    }
    layoutQuestionHeading() {
      const inner = this.querySelector('.tea-finder__inner');
      const steps = this.querySelector('.tea-finder__steps');
      const origin = inner?.getBoundingClientRect?.();
      const body = steps?.getBoundingClientRect?.();
      if (origin && body && body.height) inner.style.setProperty('--finder-desktop-photo-top',Math.round(body.top-origin.top) + 'px');
      const options = this.querySelector('[data-question="' + this.step + '"] [data-photo-options]');
      const media = this.querySelector('[data-question="' + this.step + '"] [data-photo-options] .tea-finder__choice:not([hidden]) .tea-finder__choice-media');
      const list = options?.getBoundingClientRect?.();
      const photo = media?.getBoundingClientRect?.();
      if (body && list && photo?.width) {
        const space = photo.width - (list.top-body.top) - (this.step > 0 ? 56 : 0);
        inner.style.setProperty('--finder-desktop-list-height',Math.max(0,Math.round(space)) + 'px');
      }
    }
    text(key) { return this.data.strings[key] || ''; }
    completedSteps() { return DIMENSIONS.map(key => canChoose(this.data.products,this.answers,key,this.answers[key])); }
    route(hash) { if (location.hash === hash) this.readRoute(true); else location.hash = hash; }
    readRoute(focus) {
      const requested = /^#question-([123])$/.exec(location.hash);
      if (location.hash === '#your-teas' && validAnswers(this.answers) && this.completedSteps().every(Boolean)) { this.renderResults(focus); return; }
      const incomplete = this.completedSteps().findIndex(complete => !complete);
      const allowed = incomplete < 0 ? 2 : incomplete;
      const target = requested ? Number(requested[1]) - 1 : location.hash === '#your-teas' ? allowed : 0;
      this.step = Math.min(target, allowed);
      if ((requested && this.step !== target) || location.hash === '#your-teas') history.replaceState(null, '', '#question-' + (this.step + 1));
      this.renderQuestion(focus);
    }
    choose(dimension, value) {
      if (dimension !== DIMENSIONS[this.step]) return false;
      if (!canChoose(this.data.products,this.answers,dimension,value)) return false;
      if (this.answers[dimension] !== value) {
        if (dimension === 'texture') { this.answers.aroma = ''; this.answers.taste = ''; }
        if (dimension === 'aroma') this.answers.taste = '';
      }
      this.answers[dimension] = value;
      this.route(this.step === 2 ? '#your-teas' : '#question-' + (this.step + 2)); return true;
    }
    onClick(event) {
      const choice = event.target.closest('[data-finder-choice]');
      if (choice && this.contains(choice)) { event.preventDefault(); if (!choice.disabled) this.choose(choice.dataset.dimension, choice.dataset.value); return; }
      const button = event.target.closest('[data-finder-action]');
      if (!button || !this.contains(button)) return;
      event.preventDefault(); const action = button.dataset.finderAction;
      if (action === 'back') this.route('#question-' + Math.max(1, this.step));
      if (action === 'edit') this.route('#question-1');
      if (action === 'step') this.route('#question-' + button.dataset.step);
      if (action === 'reset') { this.answers = {texture:'', aroma:'', taste:''}; this.route('#question-1'); }
    }
    setView(view) { this.querySelectorAll('[data-finder-view]').forEach(element => { element.hidden = element.dataset.finderView !== view; }); }
    focusHeading(heading, focus) {
      if (!focus) return;
      heading.focus({preventScroll:true}); this.scrollIntoView({block:'start', behavior:'instant'});
    }
    layoutPhotoChoices() {
      this.querySelectorAll('[data-photo-options]').forEach(options => {
        const choices = Array.from(options.querySelectorAll('[data-finder-choice]')).filter(button => !button.hidden);
        const geometry = photoGeometry(choices.length, this.dataset.photoLayout);
        choices.forEach((button, index) => {
          const slice = geometry[index]; if (!slice) return;
          const media = button.querySelector('.tea-finder__choice-media');
          button.querySelector('[data-choice-outline]').setAttribute('points',slice.outline);
          media.style.setProperty('--finder-slice',slice.clip);
          Object.entries(slice.bounds).forEach(([key,value]) => media.style.setProperty('--finder-image-'+key,value+'%'));
          media.style.setProperty('--finder-label-x',slice.marker.x+'%');
          media.style.setProperty('--finder-label-y',slice.marker.y+'%');
        });
      });
    }
    renderQuestion(focus) {
      this.setView('quiz'); const branches = BRANCHES[this.answers.texture] || [];
      this.querySelectorAll('[data-question]').forEach((question, index) => { question.hidden = index !== this.step; });
      this.querySelectorAll('[data-finder-choice]').forEach(button => {
        const dimension = button.dataset.dimension;
        button.hidden = dimension === 'aroma' && !branches.includes(button.dataset.value);
        const available = canChoose(this.data.products,this.answers,dimension,button.dataset.value);
        button.disabled = !available;
        button.setAttribute('aria-disabled',String(!available));
        if (available) button.removeAttribute('title'); else button.setAttribute('title',this.text('flow_unavailable'));
        button.querySelector('.tea-finder__choice-hint').textContent = available ? this.text('hint_' + dimension + '_' + button.dataset.value) : this.text('flow_unavailable');
        button.querySelectorAll('[data-choice-unavailable]').forEach(label => { label.hidden = available; });
        button.setAttribute('aria-pressed', String(this.answers[dimension] === button.dataset.value));
      });
      const activeDimension = DIMENSIONS[this.step];
      const hasUnavailable = Array.from(this.querySelectorAll('[data-finder-choice]')).some(button => button.dataset.dimension === activeDimension && !button.hidden && button.disabled);
      this.querySelectorAll('[data-choice-help]').forEach(help => { help.textContent = this.text(hasUnavailable ? 'flow_pick_available' : 'flow_pick'); });
      this.querySelector('[data-aroma-options]').style.setProperty('--finder-columns', branches.length || 4);
      this.layoutPhotoChoices();
      this.layoutQuestionHeading();
      this.querySelectorAll('[data-finder-step]').forEach((button, index) => {
        button.disabled = index > 0 && !this.completedSteps()[index - 1];
        if (index === this.step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
        const value = this.answers[DIMENSIONS[index]];
        button.querySelector('[data-step-answer]').textContent = value ? this.text(DIMENSIONS[index] + '_' + value) : '';
      });
      this.querySelector('[data-finder-progress]').textContent = this.text('flow_progress').replace('[step]', this.step + 1);
      this.querySelector('[data-finder-back]').hidden = this.step === 0;
      this.focusHeading(this.querySelector('[data-question="' + this.step + '"] [data-question-heading]'), focus);
    }
    renderResults(focus) {
      const ranked = rankProducts(this.data.products, this.answers); this.step = 2; this.setView('results');
      this.querySelector('[data-result-summary]').textContent = DIMENSIONS.map(key => this.text(key + '_' + this.answers[key])).join(' · ');
      this.querySelector('[data-partial-match]').hidden = !ranked.length || ranked[0].matches.length === 3;
      const container = this.querySelector('[data-recommendations]'); container.replaceChildren();
      ranked.forEach((item, index) => {
        const template = this.querySelector('template[data-product-id="' + item.product.id + '"]'); if (!template) return;
        const fragment = template.content.cloneNode(true);
        const matched = item.matches.map(key => this.text(key + '_' + this.answers[key])).join(' · ');
        fragment.querySelector('[data-card-reason]').textContent = this.text('flow_matches').replace('[matches]', matched);
        const image = fragment.querySelector('img');
        if (image) { image.loading = index === 0 ? 'eager' : 'lazy'; image.sizes = '(min-width: 1024px) 360px, (min-width: 768px) calc((100vw - 124px) / 2), calc(100vw - 40px)'; }
        container.append(fragment);
      });
      this.querySelector('[data-no-results]').hidden = ranked.length > 0;
      this.focusHeading(this.querySelector('[data-result-title]'), focus);
    }
  }
  if (!customElements.get('yunsho-tea-finder')) customElements.define('yunsho-tea-finder', YunshoTeaFinder);
})();
